export function useCrossAppGuard() {
  const {
    $i18n: { t },
  } = useNuxtApp();
  const globalNotification = useGlobalNotification();
  const { createCrossAppRedirectPath } = useAuthQueryParams();
  const { authStore, currentUser, refreshCurrentUser } = useAuthApi();
  const { isMultiTenant } = useIsMultiTenant();
  const plantListStore = usePlantListStore();
  const equipmentLookupListStore = useEquipmentLookupListStore();
  const currentPlant = useCurrentPlant();
  const { resolveRouteContext, makeGuardResult } = useRouteGuard();

  /**
   * @return GuardResult
   */
  const guardRouteAccess: AccessGuard = async ({ to }) => {
    const routeContext = resolveRouteContext(to, {
      matchPrefixes: {
        root: "index",
        home: "home",
      },
      queryDefaults: {
        plantId: Number,
      },
    });
    if (routeContext.isRedirectingPage) {
      return makeGuardResult(to, "continue");
    }

    if (await plantListStore.check()) {
      devConsole.info("🚦 guard rule triggered: plantListStore checked, loading equipment lookup list");
      equipmentLookupListStore.fetch();
    }

    if (authStore.isAuthed && !plantListStore.data?.length && isMultiTenant.value) {
      devConsole.error(
        "🚦 guard rule triggered: plant list is empty for authed user, redirecting to self-service",
        routeContext,
      );
      devConsole.info(" 🔀  Cross App Redirect: scenario H1 and H2: there are no plants");
      return makeGuardResult(to, {
        redirect: createCrossAppRedirectPath("sureKleanzSetup", {
          path: "/setup",
          warn: "auth.redirectReasonEmptyPlantList",
        }),
      });
    }

    if (routeContext.query.plantId) {
      devConsole.info(`🚦 guard rule triggered: load plant id ${routeContext.query.plantId}`, routeContext);
      const plant = plantListStore.data?.find((p) => p.plantId === routeContext.query.plantId);
      if (!plant) {
        devConsole.error(`🚦 guard rule triggered: plant id ${routeContext.query.plantId} not found`, routeContext);
        return makeGuardResult(to, "root");
      }

      currentPlant.setPlant(plant);
      await Promise.all([equipmentLookupListStore.fetch(), refreshCurrentUser()]);
      if (!isMultiTenant.value && (!currentUser.value || equipmentLookupListStore.rejected)) {
        devConsole.error(
          "Failed to select plant: either current user is not available or equipment lookup list fetch was rejected.",
        );
        currentPlant.clearPlant();
        globalNotification.showError(t("errors.pages.plantPicker.userNotOnPlantDatabase"));
        return makeGuardResult(to, "root");
      }
    }

    if (
      authStore.isAuthed &&
      plantListStore.data?.length &&
      isMultiTenant.value &&
      !unref(equipmentLookupListStore.initial) &&
      !unref(equipmentLookupListStore.pending) &&
      !unref(equipmentLookupListStore.data)?.length
    ) {
      devConsole.error(
        "🚦 guard rule triggered: equipment lookup list is empty or rejected for authed user, redirecting to self-service",
        routeContext,
      );
      devConsole.info(
        " 🔀  Cross App Redirect: scenario H3: equipment lookup list is empty or rejected for authed user",
      );
      return makeGuardResult(to, {
        redirect: createCrossAppRedirectPath("sureKleanzSetup", {
          warn: "auth.redirectReasonEmptyEquipmentLookupList",
        }),
      });
    }

    if (routeContext.query.plantId === currentPlant.data?.plantId) {
      devConsole.info(
        `🚦 guard rule triggered: current plant id matches query plant id, redirecting to home`,
        routeContext,
      );
      return makeGuardResult(to, { redirect: "home" });
    }

    return makeGuardResult(to, "continue");
  };

  return {
    name: "crossAppGuard",
    guardRouteAccess,
  };
}
